import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button } from "antd";
import { ChevronDown, ChevronUp } from "lucide-react";
import FileAttachment from "@/components/FileAttachment";
import VoiceRecorder from "@/components/VoiceRecorder";
import { getRestoredSectionSyncState, getSectionTaskPanelState } from "@/utility/sectionTaskState";
import { PROGRESS_PHRASES, PROGRESS_PHRASE_INTERVAL_MS } from "@/utility/sectionTaskState";

import { CloseCircleOutlined, LoadingOutlined, SendOutlined } from "@ant-design/icons";

function AgentPanel({
  agentPanelRefs,
  section,
  generatedSections,
  selectedProject,
  fileInputRef,
  isDragOver,
  setIsDragOver,
  uploadedAgentFiles,
  setUploadedAgentFiles,
  uploadingFiles,
  setUploadingFiles,
  uploadDocument,
  deleteDocument,
  deleteTag,
  regenerateDialogContent,
  textareaRef,
  inputValue,
  setInputValue,
  handleKeyPress,
  isLoading,
  handleClearInput,
  handleVoiceTranscription,
  setShowError,
  titleMap,
  transcribeAudio,
  handleSendMessage,
  logAuditAction,
  reportTemplateProjectId,
  sectionDisplayName,
}) {
  const updatesRef = useRef<HTMLDivElement | null>(null);
  const previousLifecycleRef = useRef<{ active: boolean; terminal: boolean } | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const sectionData = generatedSections.find((item) => item.id === section.id);
  const updates = sectionData?.agentUpdates || [];
  // A restored/synced section with a known terminal state
  // must show that state instead of the empty "No updates yet" placeholder.
  const { isActive, isTerminal, latestMessage, latestStateLabel, latestStateTone, latestStatus, latestUpdate } =
    getRestoredSectionSyncState(sectionData);
  const showSinglePassMessage = isActive && updates.length === 0;
  const showStatus = isActive || isTerminal || updates.length > 0;

  // While the section is active but has not yet
  // produced its first concrete update, cycle through action-specific
  // progress copy so the user sees motion instead of a static
  // "Waiting for an update..." string. The interval is owned by the
  // panel and is cleared the moment we leave the showSinglePassMessage
  // state, so terminal messages (e.g. "Section generated.") replace
  // it without a flash.
  const [progressIndex, setProgressIndex] = useState(0);
  useEffect(() => {
    if (!showSinglePassMessage) return;
    const id = window.setInterval(() => {
      setProgressIndex((idx) => (idx + 1) % PROGRESS_PHRASES.length);
    }, PROGRESS_PHRASE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [showSinglePassMessage]);
  const progressMessage = PROGRESS_PHRASES[progressIndex] ?? PROGRESS_PHRASES[0];

  useLayoutEffect(() => {
    if (isCollapsed) return;

    const updatesPanel = updatesRef.current;
    if (!updatesPanel) return;

    let frame = 0;
    const scrollToLatest = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        updatesPanel.scrollTop = updatesPanel.scrollHeight;
      });
    };

    scrollToLatest();

    if (typeof ResizeObserver === "undefined") {
      return () => window.cancelAnimationFrame(frame);
    }

    const observer = new ResizeObserver(scrollToLatest);
    observer.observe(updatesPanel);
    if (updatesPanel.firstElementChild) observer.observe(updatesPanel.firstElementChild);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [updates.length, latestUpdate?.message, latestUpdate?.status, isCollapsed]);

  useEffect(() => {
    const previousLifecycle = previousLifecycleRef.current;

    if (previousLifecycle === null) {
      // Restored terminal streams start compact; active streams start open.
      setIsCollapsed(isTerminal);
    } else if (!previousLifecycle.active && isActive) {
      // A new generation or regeneration lifecycle always starts open.
      setIsCollapsed(false);
    } else if (!isActive && isTerminal && (previousLifecycle.active || !previousLifecycle.terminal)) {
      // Collapse once after the active lifecycle reaches its terminal state.
      setIsCollapsed(true);
    }

    previousLifecycleRef.current = { active: isActive, terminal: isTerminal };
  }, [isActive, isTerminal]);

  const toggleCollapse = () => setIsCollapsed((collapsed) => !collapsed);

  return (
    <div
      className={`section_generator_panel_generated_eval_container section_generator_panel_generated_eval_container--agent ${
        isCollapsed ? "section_generator_panel_generated_eval_container--collapsed" : ""
      }`}
      ref={(el) => {
        if (el) {
          agentPanelRefs.current.set(section.id, el);
        } else {
          agentPanelRefs.current.delete(section.id);
        }
      }}
    >
      {showStatus && (
        <button
          aria-expanded={!isCollapsed}
          aria-label={`${isCollapsed ? "Expand" : "Collapse"} section generation status`}
          className={`section_generator_panel_generated_eval_disclosure section_generator_panel_generated_eval_disclosure--${latestStateTone}`}
          onClick={toggleCollapse}
          title={`${isCollapsed ? "Expand" : "Collapse"} status`}
          type="button"
        >
          <span className="section_generator_panel_generated_eval_disclosure_status">
            <span
              className={`section_generator_panel_generated_eval_disclosure_dot section_generator_panel_generated_eval_disclosure_dot--${latestStateTone}`}
              aria-hidden="true"
            />
            <span>{latestStateLabel}</span>
          </span>
          <span
            className="section_generator_panel_generated_eval_disclosure_message"
            title={showSinglePassMessage ? progressMessage : latestMessage}
          >
            {showSinglePassMessage ? progressMessage : latestMessage}
          </span>
          {isCollapsed ? (
            <ChevronDown size={12} strokeWidth={2.25} aria-hidden="true" />
          ) : (
            <ChevronUp size={12} strokeWidth={2.25} aria-hidden="true" />
          )}
        </button>
      )}
      {!isCollapsed && updates.length > 0 ? (
        <div className="section_generator_panel_generated_eval_updates" ref={updatesRef}>
          <div className="section_generator_panel_generated_eval_updates_content">
            {updates.map((update, index) => {
              const normalizedStatus = update?.status?.toLowerCase();
              const isDone = normalizedStatus === "done" || normalizedStatus === "completed";
              const isFailed = ["error", "failed", "cancelled"].includes(normalizedStatus);
              const statusLabel = isDone ? "Done" : isFailed ? "Failed" : "Running";
              const statusColor = isDone ? "#008000" : isFailed ? "#b42318" : "#DAA520";

              return (
                <div key={index}>
                  <div>
                    {update?.message && (
                      <div>
                        <strong
                          style={{
                            color: statusColor,
                          }}
                        >{`${statusLabel}: `}</strong>
                        {update.message}
                      </div>
                    )}
                  </div>
                  {update.status === "file_input" && (
                    <FileAttachment
                      selectedProject={selectedProject}
                      fileInputRef={fileInputRef}
                      isDragOver={isDragOver}
                      setIsDragOver={setIsDragOver}
                      uploadedFiles={uploadedAgentFiles}
                      setUploadedFiles={setUploadedAgentFiles}
                      uploadingFiles={uploadingFiles}
                      setUploadingFiles={setUploadingFiles}
                      uploadDocument={uploadDocument}
                      deleteDocument={deleteDocument}
                      deleteTag={deleteTag}
                      regenerateDialogContent={regenerateDialogContent}
                      type={"agent"}
                      id={section.id}
                      logAuditAction={logAuditAction}
                      reportTemplateProjectId={reportTemplateProjectId}
                      sectionDisplayName={sectionDisplayName}
                    />
                  )}

                  {update.status === "text_input" && (
                    <div className="llm-chat-input-wrapper">
                      <textarea
                        ref={textareaRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={"Answer..."}
                        className="llm-chat-input"
                        rows={1}
                        disabled={isLoading}
                      />
                      {document.activeElement === textareaRef.current && inputValue.trim() && (
                        <Button onClick={handleClearInput} className="llm-chat-clear-button">
                          {!isLoading && <CloseCircleOutlined style={{ fontSize: "0.75rem" }} />}
                        </Button>
                      )}
                      <VoiceRecorder
                        onTranscriptionComplete={handleVoiceTranscription}
                        disabled={isLoading}
                        className="llm-chat-mic-button"
                        setShowError={setShowError}
                        titleMap={titleMap}
                        transcribeAudio={transcribeAudio}
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || isLoading}
                        className="llm-chat-send-button"
                      >
                        {isLoading ? (
                          <LoadingOutlined spin style={{ fontSize: "1rem" }} />
                        ) : (
                          <SendOutlined style={{ fontSize: "0.9rem" }} />
                        )}
                      </Button>
                    </div>
                  )}
                  {update.status === "bool_input" && <></>}
                  {update.status === "select_input" && <></>}
                </div>
              );
            })}
          </div>
        </div>
      ) : !isCollapsed && showSinglePassMessage ? (
        <span style={{ fontSize: "0.85rem" }}>
          {/* Explain the single-pass model up front so
              users know generation is one pass and what to expect. */}
          First pass running — Jenly generates this section in a single pass.
          Updates will appear here as they arrive.
        </span>
      ) : null}
    </div>
  );
}

export default AgentPanel;
